import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Sparkles, ChevronDown, LogOut, ShieldCheck, Menu, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { ScoreHeaderPastille } from "@/components/park/commun";

export default function Header({ onCopilot, onMenu, nbSuggestions, enLigne }) {
  const navigate = useNavigate();
  const { auth, logout } = useAuth();
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(false);
  const [notifOuvert, setNotifOuvert] = useState(false);
  const [notifs, setNotifs] = useState({ total_non_lues: 0, notifications: [] });
  const menuRef = useRef(null);
  const notifRef = useRef(null);

  const chargerNotifs = async () => {
    try {
      const r = await api.get("/notifications?limite=20");
      setNotifs(r.data || { total_non_lues: 0, notifications: [] });
    } catch (_) {}
  };

  useEffect(() => {
    chargerNotifs();
    const t = setInterval(chargerNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOuvert(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const chercher = (e) => {
    if (e.key === "Enter" && q.trim()) {
      navigate(`/clients?q=${encodeURIComponent(q.trim())}`);
      setQ("");
    }
  };

  const marquerLue = async (id) => {
    try {
      await api.patch(`/notifications/${id}/lue`);
      chargerNotifs();
    } catch (_) {}
  };

  const marquerToutLu = async () => {
    try {
      await api.post("/notifications/tout-lu");
      chargerNotifs();
    } catch (_) {}
  };

  const ouvrirNotif = (n) => {
    marquerLue(n.id);
    setNotifOuvert(false);
    if (n.url) navigate(n.url);
  };

  const nom = auth?.user?.nom || "";
  const initiales = nom.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-30 bg-[#F6F7F9]/90 backdrop-blur border-b border-[--idc-border] px-3 sm:px-7 py-3 flex items-center gap-2 sm:gap-4" data-testid="header">
      <button className="lg:hidden p-2 rounded-lg hover:bg-white transition-colors shrink-0" onClick={onMenu} data-testid="header-menu-btn" aria-label="Menu">
        <Menu className="w-5 h-5 text-[--idc-text]" />
      </button>
      <div className="relative flex-1 max-w-md hidden md:block">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[--idc-muted]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={chercher}
          placeholder="Client, immat., OR, facture…"
          className="w-full bg-white border border-[--idc-border] rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#2F6FED]/30"
          data-testid="header-search-input"
        />
      </div>
      <button className="md:hidden p-2 rounded-full hover:bg-white transition-colors" onClick={() => navigate("/clients")} data-testid="header-search-mobile-btn" aria-label="Rechercher">
        <Search className="w-[18px] h-[18px] text-[--idc-muted]" />
      </button>
      <div className="flex-1" />
      <div className="relative" ref={notifRef}>
        <button
          className="relative p-2 rounded-full hover:bg-white transition-colors"
          onClick={() => setNotifOuvert(!notifOuvert)}
          data-testid="header-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="w-[18px] h-[18px] text-[--idc-muted]" />
          {notifs.total_non_lues > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-[--idc-danger] text-white text-[10px] font-bold flex items-center justify-center px-1">
              {notifs.total_non_lues > 9 ? "9+" : notifs.total_non_lues}
            </span>
          )}
        </button>
        {notifOuvert && (
          <div className="absolute right-0 top-full mt-1.5 w-[380px] idc-card p-0 shadow-lg z-50" data-testid="notif-menu">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[--idc-border]">
              <div className="font-semibold text-[13px]">Notifications</div>
              {notifs.total_non_lues > 0 && (
                <button
                  onClick={marquerToutLu}
                  className="text-[11px] text-[#2F6FED] hover:underline"
                  data-testid="notif-tout-lu-btn"
                >
                  Tout marquer comme lu
                </button>
              )}
            </div>
            <div className="max-h-[420px] overflow-y-auto">
              {notifs.notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-[13px] text-[--idc-muted]">
                  Aucune notification
                </div>
              ) : (
                notifs.notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => ouvrirNotif(n)}
                    className={`w-full text-left px-4 py-3 border-b border-[--idc-border] hover:bg-[#F1F4F9] transition-colors ${!n.lue ? "bg-[#EFF6FF]" : ""}`}
                    data-testid={`notif-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${!n.lue ? "bg-[--idc-danger]" : "bg-transparent"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[13px] leading-tight">{n.titre}</div>
                        <div className="text-[12px] text-[--idc-muted] mt-1 line-clamp-2">{n.message}</div>
                        <div className="text-[10.5px] text-[--idc-muted] mt-1">
                          {new Date(n.date).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      <ScoreHeaderPastille />
      <button onClick={onCopilot} className="copilot-btn flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-semibold" data-testid="header-copilot-btn">
        <Sparkles className="w-4 h-4" />
        <span className="hidden sm:inline">ID Copilot</span>
        {nbSuggestions > 0 && (
          <span className="bg-white text-[#16326E] rounded-full text-[11px] font-bold min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {nbSuggestions}
          </span>
        )}
      </button>
      <div className="relative" ref={menuRef}>
        <button className="flex items-center gap-2.5 pl-2 rounded-lg hover:bg-white py-1 pr-1.5 transition-colors" onClick={() => setMenu(!menu)} data-testid="header-user">
          <div className="w-9 h-9 rounded-full bg-[#16326E] text-white flex items-center justify-center font-bold text-xs">{initiales || "?"}</div>
          <div className="leading-tight hidden md:block text-left">
            <div className="text-[13px] font-semibold">{nom}</div>
            <div className="text-[11px] text-[--idc-muted]">{auth?.roleLabel}{!enLigne && " · Copilot local"}</div>
          </div>
          <ChevronDown className="w-4 h-4 text-[--idc-muted]" />
        </button>
        {menu && (
          <div className="absolute right-0 top-full mt-1.5 w-56 idc-card p-1.5 shadow-lg z-50" data-testid="header-user-menu">
            <div className="px-3 py-2 border-b border-[--idc-border]">
              <div className="text-[13px] font-semibold">{nom}</div>
              <div className="text-[11.5px] text-[--idc-muted]">{auth?.user?.email}</div>
              <div className="text-[11px] text-[--idc-muted] mt-0.5">{auth?.roleLabel} · {auth?.user?.site}</div>
            </div>
            {auth?.user?.role === "admin" && (
              <button className="w-full flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg hover:bg-[#F1F4F9] transition-colors"
                onClick={() => { setMenu(false); navigate("/administration"); }} data-testid="menu-administration-btn">
                <ShieldCheck className="w-4 h-4 text-[--idc-muted]" /> Administration
              </button>
            )}
            <button className="w-full flex items-center gap-2 px-3 py-2 text-[13px] rounded-lg hover:bg-[#FEE2E2] text-[#B91C1C] transition-colors"
              onClick={() => { setMenu(false); logout(); }} data-testid="menu-deconnexion-btn">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
